import { getHistoryActions } from "../stores/historyStore";
import { getSceneState } from "../stores/sceneStore";
import { useSelectionStore } from "../stores/selectionStore";
import { getViewerActions, getViewerState } from "../stores/viewerStore";
import { setRotationGizmoVisible } from "../selected-body-transform-gizmo/rotationGizmoVisibility";
import { setScaleGizmoVisible } from "../selected-body-transform-gizmo/scaleGizmoVisibility";
import { duplicateBodySubtree } from "./bodyDuplication";
import { duplicateBodyGeom } from "./geomDuplication";
import { findGeomNode } from "./geomSelection";
import type { AllActions } from "../stores/sceneActions/actionTypes";
import type { Body, BodyUUID, SiteUUID } from "../types/scene";

let autoPositionGizmoBodyUuid: string | null = null;
let unsubscribeAutoPositionGizmoSelection: (() => void) | null = null;

function clearAutoPositionGizmoTracking(): void {
  autoPositionGizmoBodyUuid = null;
  if (unsubscribeAutoPositionGizmoSelection) {
    unsubscribeAutoPositionGizmoSelection();
    unsubscribeAutoPositionGizmoSelection = null;
  }
}

function selectionIncludesBody(
  selection: ReturnType<typeof useSelectionStore.getState>,
  bodyUuid: string
): boolean {
  return selection.selectedBody === bodyUuid || selection.selectedBodies.includes(bodyUuid);
}

function startAutoPositionGizmoSelectionTracking(bodyUuid: string): void {
  clearAutoPositionGizmoTracking();
  autoPositionGizmoBodyUuid = bodyUuid;
  unsubscribeAutoPositionGizmoSelection = useSelectionStore.subscribe((selection) => {
    const targetBodyUuid = autoPositionGizmoBodyUuid;
    if (!targetBodyUuid || selectionIncludesBody(selection, targetBodyUuid)) return;
    if (getViewerState().showPositionGizmo) {
      getViewerActions().toggleShowPositionGizmo();
    }
    clearAutoPositionGizmoTracking();
  });
}

/** Switch transform tools to Blender-style duplicate placement: move gizmo on, other gizmos off. */
export function activateMoveGizmoForDuplicatedBody(bodyUuid: string): void {
  setRotationGizmoVisible(false);
  setScaleGizmoVisible(false);
  const positionGizmoWasVisible = getViewerState().showPositionGizmo;
  if (!positionGizmoWasVisible) {
    getViewerActions().toggleShowPositionGizmo();
  }
  if (!positionGizmoWasVisible || autoPositionGizmoBodyUuid) {
    startAutoPositionGizmoSelectionTracking(bodyUuid);
  } else {
    clearAutoPositionGizmoTracking();
  }
}

/** Switch transform tools to duplicate placement for the current non-body selection. */
export function activateMoveGizmoForDuplicatedSelection(): void {
  setRotationGizmoVisible(false);
  setScaleGizmoVisible(false);
  if (!getViewerState().showPositionGizmo) {
    getViewerActions().toggleShowPositionGizmo();
  }
  clearAutoPositionGizmoTracking();
}

/** Grouped history + duplicate body subtree (body-only or full branch). */
export function runDuplicateBodySubtreeGrouped(
  sceneActions: AllActions,
  sourceBodyUuid: BodyUUID,
  includeBranch: boolean
): string | null {
  const parentUuid = sceneActions.getBodyParent(sourceBodyUuid);
  const historyActions = getHistoryActions();
  historyActions.beginGroup(getSceneState());
  try {
    return duplicateBodySubtree({
      sourceBodyUuid,
      targetParentUuid: parentUuid,
      includeBranch,
      getSceneState,
      sceneActions,
    });
  } finally {
    historyActions.endGroup(getSceneState());
  }
}

function bodyDepth(bodies: Record<BodyUUID, Body>, bodyUuid: BodyUUID): number {
  let depth = 0;
  let currentUuid: BodyUUID | null = bodyUuid;
  const visited = new Set<string>();
  while (currentUuid && !visited.has(currentUuid)) {
    visited.add(currentUuid);
    const parentUuid = Object.values(bodies || {}).find((body) =>
      body?.children?.includes(currentUuid as BodyUUID)
    )?.uuid;
    if (!parentUuid) break;
    depth += 1;
    currentUuid = parentUuid;
  }
  return depth;
}

/** Grouped history + duplicate a set of selected bodies, preserving selected-internal parenting. */
export function runDuplicateBodySelectionGrouped(
  sceneActions: AllActions,
  sourceBodyUuids: BodyUUID[]
): string[] {
  const uniqueSourceUuids = Array.from(new Set(sourceBodyUuids.filter(Boolean)));
  if (uniqueSourceUuids.length === 0) return [];

  const historyActions = getHistoryActions();
  historyActions.beginGroup(getSceneState());
  try {
    const sceneState = getSceneState();
    const bodies = (sceneState?.bodies || {}) as Record<BodyUUID, Body>;
    const sourceSet = new Set<string>(uniqueSourceUuids);
    const copyBySourceUuid = new Map<string, string>();
    const orderedSourceUuids = uniqueSourceUuids
      .filter((uuid) => bodies[uuid])
      .sort((a, b) => bodyDepth(bodies, a) - bodyDepth(bodies, b));

    orderedSourceUuids.forEach((sourceBodyUuid) => {
      const originalParentUuid = sceneActions.getBodyParent(sourceBodyUuid);
      const targetParentUuid =
        originalParentUuid && sourceSet.has(originalParentUuid)
          ? (copyBySourceUuid.get(originalParentUuid) as BodyUUID | undefined) || originalParentUuid
          : originalParentUuid;
      const newUuid = duplicateBodySubtree({
        sourceBodyUuid,
        targetParentUuid,
        includeBranch: false,
        getSceneState,
        sceneActions,
      });
      if (newUuid) copyBySourceUuid.set(sourceBodyUuid, newUuid);
    });

    return orderedSourceUuids
      .map((sourceBodyUuid) => copyBySourceUuid.get(sourceBodyUuid))
      .filter(Boolean) as string[];
  } finally {
    historyActions.endGroup(getSceneState());
  }
}

/** Duplicate any geom from a `bodyUuid:geomUuid` key; returns the new geom UUID or null. */
export function duplicateGeomFromKey(
  sceneActions: AllActions,
  bodies: Record<BodyUUID, Body>,
  sourceGeomKey: string
): string | null {
  const resolved = findGeomNode(bodies, sourceGeomKey);
  if (!resolved?.bodyUuid) return null;
  return duplicateBodyGeom({
    sourceGeomKey,
    targetBodyUuid: resolved.bodyUuid,
    getSceneState,
    sceneActions,
  });
}

/**
 * Duplicate a collision geom from a `bodyUuid:geomUuid` key; returns the new geom UUID or null.
 * Skips visual-only geoms.
 */
export function duplicateCollisionGeomFromKey(
  sceneActions: AllActions,
  bodies: Record<BodyUUID, Body>,
  sourceGeomKey: string
): string | null {
  const resolved = findGeomNode(bodies, sourceGeomKey);
  if (!resolved?.bodyUuid || resolved?.geom?.visualOnly === true) return null;
  return duplicateGeomFromKey(sceneActions, bodies, sourceGeomKey);
}

/** Selection key for the duplicated collision geom (`bodyUuid:newGeomUuid`). */
export function geomSelectionKeyAfterDuplicate(
  sourceGeomKey: string,
  newGeomUuid: string | null
): string | null {
  if (!newGeomUuid) return null;
  const sepIdx = sourceGeomKey.indexOf(":");
  const bodyUuid = sepIdx > 0 ? sourceGeomKey.slice(0, sepIdx) : null;
  return bodyUuid ? `${bodyUuid}:${newGeomUuid}` : null;
}

export const collisionGeomSelectionKeyAfterDuplicate = geomSelectionKeyAfterDuplicate;

/** Grouped history + duplicate a site on its current parent body. */
export function duplicateSiteGrouped(
  sceneActions: AllActions,
  sourceSiteUuid: SiteUUID
): string | null {
  if (!sourceSiteUuid) return null;
  const historyActions = getHistoryActions();
  historyActions.beginGroup(getSceneState());
  try {
    const sceneState = getSceneState();
    const sourceSite = sceneState?.sites?.[sourceSiteUuid] || null;
    if (!sourceSite) return null;
    const parentBodyUuid =
      Object.values(sceneState?.bodies || {}).find((body) => body?.sites?.includes(sourceSiteUuid))
        ?.uuid || null;
    const { uuid: _uuid, ...siteInput } = sourceSite;
    return sceneActions.addSite(siteInput, parentBodyUuid);
  } finally {
    historyActions.endGroup(getSceneState());
  }
}
